import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { ApolloError as ApolloErrorConstructor } from '@apollo/client';
import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { useToggle } from '@infl-ts/util-hooks';
import { makeApolloError } from '@infl-ts/util-make-apollo-decoded';
import { useFocusErrors } from '@infl-ts/util-react-hook-form';
import { useMutationMapper } from '@infl-ts/util-use-mutation-mapper';
import { Box, Fade, FormControl, Typography } from '@mui/material';
import * as DE from '@nll/datum/lib/DatumEither';
import * as A from 'fp-ts/Array';
import * as E from 'fp-ts/Either';
import { flow, pipe } from 'fp-ts/function';
import * as R from 'fp-ts/Record';
import { useContext, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AsyncButton, SaveStates } from '../../async-button';
import { TextFormField } from '../../form';
import { SaveButton } from '../../save-button';
import { changePasswordFailure, resetPasswordFailure } from './alerts';
import { ChangePasswordBox } from './change-password-box';
import { useChangePasswordMutation, useResetPasswordMutation } from './mutations';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var PureChangePasswordSection = function PureChangePasswordSection(_ref) {
  var changePasswordForm = _ref.changePasswordForm,
      handleChangePassword = _ref.handleChangePassword,
      handleResetPassword = _ref.handleResetPassword,
      passwordWasReset = _ref.passwordWasReset,
      changePasswordStatus = _ref.changePasswordStatus,
      forgotPasswordState = _ref.forgotPasswordState;
  var control = changePasswordForm.control,
      handleSubmit = changePasswordForm.handleSubmit,
      getValues = changePasswordForm.getValues,
      errors = changePasswordForm.formState.errors;
  var Saving = SaveStates.Saving,
      Default = SaveStates.Default,
      Success = SaveStates.Success,
      Error = SaveStates.Error;
  var mutationValue = useMutationMapper(changePasswordStatus, function (_ref2) {
    var editProfile = _ref2.editProfile;
    return editProfile.__typename === 'EditProfileErrors' ? E.left(editProfile) : E.right(editProfile);
  }, [Saving, R.isEmpty(changePasswordForm.formState.dirtyFields) ? Success : Default, Error, Default]);
  var forgotPasswordStatus = useMutationMapper(forgotPasswordState, function (_ref3) {
    var resetPassword = _ref3.resetPassword;
    return resetPassword.__typename === 'ResetPasswordError' ? E.left(resetPassword) : E.right(resetPassword);
  }, [Saving, Success, Error, Default]);

  var _useFocusErrors = useFocusErrors(),
      questionRef = _useFocusErrors.questionRef,
      focusOnFirstError = _useFocusErrors.focusOnFirstError;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/_jsxs(ChangePasswordBox, {
    questionRef: questionRef,
    "data-element": "ChangePasswordBox",
    "data-component": "PureChangePasswordSection",
    "data-source-file": "change-password.tsx",
    children: [/*#__PURE__*/_jsxs(FormControl, {
      id: "oldPassword",
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-component": "PureChangePasswordSection",
      "data-source-file": "change-password.tsx",
      children: [/*#__PURE__*/_jsx(TextFormField, {
        name: "oldPassword",
        label: t('Current password'),
        control: control,
        required: true,
        type: "password",
        autoComplete: "current-password",
        error: errors.oldPassword,
        "data-element": "TextFormField",
        "data-component": "PureChangePasswordSection",
        "data-source-file": "change-password.tsx"
      }), /*#__PURE__*/_jsx(SaveButton, {
        saveState: forgotPasswordStatus,
        onClick: handleResetPassword,
        variant: "text",
        sx: function sx(t) {
          return {
            mt: 1,
            px: 0,
            width: 'fit-content',
            fontWeight: t.typography.fontWeightRegular
          };
        },
        buttonLabels: {
          saving: t('Processing...'),
          "default": t('Forgot password?'),
          success: t('Forgot password?'),
          error: t('Forgot password?')
        },
        "data-element": "SaveButton",
        "data-source-file": "change-password.tsx"
      }), /*#__PURE__*/_jsx(Fade, {
        "in": passwordWasReset,
        "data-element": "Fade",
        "data-source-file": "change-password.tsx",
        children: /*#__PURE__*/_jsx(Typography, {
          variant: "caption",
          "data-element": "Typography",
          "data-source-file": "change-password.tsx",
          children: t('You will receive an email with instructions for how to confirm your account in a few minutes.')
        })
      })]
    }), /*#__PURE__*/_jsx(FormControl, {
      id: "password",
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-source-file": "change-password.tsx",
      children: /*#__PURE__*/_jsx(TextFormField, {
        name: "password",
        label: t('New password'),
        control: control,
        error: errors.password,
        type: "password",
        autoComplete: "off",
        required: true,
        "data-element": "TextFormField",
        "data-source-file": "change-password.tsx"
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      id: "passwordConfirmation",
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-source-file": "change-password.tsx",
      children: /*#__PURE__*/_jsx(TextFormField, {
        name: "passwordConfirmation",
        label: t('Confirm password'),
        control: control,
        error: errors.passwordConfirmation,
        errorMessage: "New password and confirmation must match",
        required: true,
        type: "password",
        autoComplete: "off",
        rules: {
          validate: function validate(value) {
            return getValues('password') === value;
          }
        },
        "data-element": "TextFormField",
        "data-source-file": "change-password.tsx"
      })
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        my: 1,
        width: 1,
        display: 'flex',
        justifyContent: 'flex-end'
      },
      "data-element": "Box",
      "data-source-file": "change-password.tsx",
      children: /*#__PURE__*/_jsx(AsyncButton, {
        status: mutationValue,
        onClick: handleSubmit(handleChangePassword, focusOnFirstError),
        sx: {
          mt: 1,
          cursor: 'pointer',
          textDecoration: 'none'
        },
        text: ['Changing password...', 'Password changed', 'Change password', 'Change password'],
        "data-element": "AsyncButton",
        "data-source-file": "change-password.tsx"
      })
    })]
  });
};

var useProps = function useProps(_ref4) {
  var name = _ref4.name;

  var _useState = useState(DE.initial),
      forgotPasswordState = _useState[0],
      setForgotPasswordState = _useState[1];

  var changePasswordForm = useForm({
    defaultValues: {
      oldPassword: '',
      password: '',
      passwordConfirmation: ''
    }
  });
  var watchObject = useWatch({
    control: changePasswordForm.control
  });
  useEffect(function () {
    setForgotPasswordState(DE.initial);
  }, [watchObject]);

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var _useTranslation2 = useTranslation(),
      t = _useTranslation2.t;

  var setErrors = function setErrors(errors, setError) {
    return pipe(errors, A.map(function (error) {
      if (error.__typename === 'PasswordValidationError' || error.__typename === 'CannotReusePasswordError') {
        setError('password', error);
      } else {
        setError('oldPassword', {
          message: 'Incorrect Password'
        });
      }
    }));
  };

  var _useChangePasswordMut = useChangePasswordMutation({
    onCompleted: E.match(function () {
      return showAlert(changePasswordFailure(handleChangePassword, t));
    }, function (_ref5) {
      var editProfile = _ref5.editProfile;
      if (editProfile.__typename === 'EditProfileResult') changePasswordForm.reset();else if (editProfile.errors[0].__typename === 'NotFoundError' || editProfile.errors[0].__typename === 'UnprocessableEntityError') showAlert(changePasswordFailure(handleChangePassword, t));else setErrors(editProfile.errors, changePasswordForm.setError);
    })
  }),
      _useChangePasswordMut2 = _slicedToArray(_useChangePasswordMut, 2),
      changePassword = _useChangePasswordMut2[0],
      changePasswordStatus = _useChangePasswordMut2[1];

  var _useToggle = useToggle(),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      passwordWasReset = _useToggle2[0],
      setPasswordWasReset = _useToggle2[1].on;

  var _useResetPasswordMuta = useResetPasswordMutation({
    onCompleted: E.match(function () {
      setForgotPasswordState(resetPasswordStatus);
      showAlert(resetPasswordFailure(handleResetPassword, t));
    }, function (_ref6) {
      var resetPassword = _ref6.resetPassword;

      if (resetPassword.__typename === 'ResetPasswordResult') {
        setPasswordWasReset();
      } else if (resetPassword.errors[1].__typename === 'NotFoundError' || resetPassword.errors[1].__typename === 'UnprocessableEntityError') {
        showAlert(resetPasswordFailure(handleResetPassword, t));
        setForgotPasswordState(DE.failure(makeApolloError(new ApolloErrorConstructor({
          errorMessage: 'failed to reset password'
        }))));
      } else {
        setForgotPasswordState(DE.failure(makeApolloError(new ApolloErrorConstructor({
          errorMessage: 'failed to reset password'
        }))));
      }
    })
  }),
      _useResetPasswordMuta2 = _slicedToArray(_useResetPasswordMuta, 2),
      resetPassword = _useResetPasswordMuta2[0],
      resetPasswordStatus = _useResetPasswordMuta2[1];

  useEffect(function () {
    if (DE.isPending(resetPasswordStatus) || DE.isRefresh(resetPasswordStatus)) setForgotPasswordState(resetPasswordStatus);
  }, [resetPasswordStatus]);

  var handleChangePassword = function handleChangePassword() {
    return changePassword({
      variables: {
        input: {
          name: name,
          newPassword: {
            password: changePasswordForm.getValues('password'),
            oldPassword: changePasswordForm.getValues('oldPassword')
          }
        }
      }
    });
  };

  var handleResetPassword = function handleResetPassword() {
    return resetPassword();
  };

  return {
    changePasswordForm: changePasswordForm,
    handleChangePassword: handleChangePassword,
    handleResetPassword: handleResetPassword,
    passwordWasReset: passwordWasReset,
    changePasswordStatus: changePasswordStatus,
    forgotPasswordState: forgotPasswordState
  };
};

export default Object.assign(flow(useProps, PureChangePasswordSection), {
  displayName: 'ChangePasswordSection'
});